import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import BoostMeCard from './components/BoostMeCard';
import Banner from './components/Banner';
import argFlag from './assets/argFlag.png';
import euroFlag from './assets/euroFlag.png';
import satFlag from './assets/satFlag.png';
import usdFlag from './assets/usdFlag.png';
import btcFlag from './assets/btcFlag.png';
import ukFlag from './assets/ukFlag.png';
import brFlag from './assets/brFlag.png';
import canFlag from './assets/canFlag.png';
import colFlag from './assets/colFlag.png';
import mexFlag from './assets/mexFlag.png';
import useExchangeRates from './hooks/useExchangeRates';

// Configuración de monedas fiat disponibles. `flag` es una imagen PNG y,
// cuando no hay imagen, se usa `emoji` como bandera.
const CURRENCIES = [
  { code: 'USD', flag: usdFlag },
  { code: 'EUR', flag: euroFlag },
  { code: 'GBP', flag: ukFlag },
  { code: 'ARS', flag: argFlag },
  { code: 'BRL', flag: brFlag },
  { code: 'CAD', flag: canFlag },
  { code: 'COP', flag: colFlag },
  { code: 'MXN', flag: mexFlag },
  { code: 'UYU', emoji: '🇺🇾' },
  { code: 'PEN', emoji: '🇵🇪' },
];

const CurrencyFlag = ({ code }) => {
  const cfg = CURRENCIES.find((c) => c.code === code);
  if (!cfg) return null;
  if (cfg.flag) return <img src={cfg.flag} alt={`${code} icon`} />;
  return (
    <span className="flag-emoji" role="img" aria-label={`${code} icon`}>
      {cfg.emoji}
    </span>
  );
};

const SunIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MonitorIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

const MoonIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
  </svg>
);

const THEME_OPTIONS = [
  { mode: 'light', label: 'Modo claro', Icon: SunIcon },
  { mode: 'system', label: 'Según el sistema', Icon: MonitorIcon },
  { mode: 'dark', label: 'Modo oscuro', Icon: MoonIcon },
];

const ThemeSwitch = ({ value, onChange }) => (
  <div className="theme-switch" role="group" aria-label="Tema">
    {THEME_OPTIONS.map(({ mode, label, Icon }) => (
      <button
        key={mode}
        type="button"
        className={`theme-switch__btn${value === mode ? ' is-active' : ''}`}
        onClick={() => onChange(mode)}
        aria-label={label}
        aria-pressed={value === mode}
        title={label}
      >
        <Icon />
      </button>
    ))}
  </div>
);

const SATS_PER_BTC = 100000000;

function App() {
  const [amountFiat, setAmountFiat] = useState('');
  const [amountBtc, setAmountBtc] = useState('');
  const [amountSats, setAmountSats] = useState('');
  const [amountFiatCompare, setAmountFiatCompare] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [compareCurrency, setCompareCurrency] = useState(null);
  const [onChainFee, setOnChainFee] = useState(null);
  const [activeField, setActiveField] = useState(null);

  // themeMode: 'light' | 'dark' | 'system'
  const [themeMode, setThemeMode] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark' || saved === 'light' || saved === 'system') return saved;
    } catch {
      /* noop */
    }
    return 'system';
  });

  useEffect(() => {
    const prefersDark =
      window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = () => {
      const effective =
        themeMode === 'system'
          ? prefersDark && prefersDark.matches
            ? 'dark'
            : 'light'
          : themeMode;
      document.documentElement.setAttribute('data-theme', effective);
    };

    applyTheme();

    try {
      localStorage.setItem('theme', themeMode);
    } catch {
      /* noop */
    }

    if (themeMode === 'system' && prefersDark) {
      prefersDark.addEventListener('change', applyTheme);
      return () => prefersDark.removeEventListener('change', applyTheme);
    }
  }, [themeMode]);

  // Lista de monedas cuyas tasas necesitamos (principal + comparación).
  const neededCurrencies = useMemo(
    () => [currency, compareCurrency].filter(Boolean),
    [currency, compareCurrency]
  );

  const { rates, isLoading, error } = useExchangeRates(neededCurrencies);

  const formatNumber = useCallback((num) => {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  }, []);

  const clearFields = useCallback(() => {
    setAmountFiat('');
    setAmountBtc('');
    setAmountSats('');
    setAmountFiatCompare('');
    setActiveField(null);
  }, []);

  // Núcleo de la conversión: a partir del campo "ancla" (el que el usuario
  // está usando) se deriva el valor en BTC y luego se recalculan los demás
  // campos. Así nunca se rompe la actualización automática.
  const recalcFrom = useCallback(
    (field, rawValue) => {
      const rate = rates[currency];
      const rateCmp = compareCurrency ? rates[compareCurrency] : null;

      let btc = null;

      if (field === 'sats') {
        const digits = rawValue.replace(/\./g, '').replace(/,/g, '');
        if (digits === '') {
          clearFields();
          return;
        }
        const sats = Number(digits);
        if (Number.isNaN(sats)) return;
        btc = sats / SATS_PER_BTC;
      } else if (field === 'btc') {
        const raw = rawValue.replace(',', '.');
        if (raw === '') {
          clearFields();
          return;
        }
        if (!/^\d*\.?\d*$/.test(raw)) return;
        btc = parseFloat(raw);
        if (Number.isNaN(btc)) return;
      } else if (field === 'fiat') {
        const v = rawValue.replace(/[^0-9,]/g, '');
        if (v === '') {
          clearFields();
          return;
        }
        const num = parseFloat(v.replace(',', '.'));
        if (Number.isNaN(num) || !rate) return;
        btc = num / rate;
      } else if (field === 'fiatCompare') {
        const v = rawValue.replace(/[^0-9,]/g, '');
        if (v === '') {
          clearFields();
          return;
        }
        const num = parseFloat(v.replace(',', '.'));
        if (Number.isNaN(num) || !rateCmp) return;
        btc = num / rateCmp;
      }

      if (btc === null || Number.isNaN(btc)) return;

      const sats = Math.round(btc * SATS_PER_BTC);

      if (field !== 'sats') setAmountSats(sats.toLocaleString('de-DE'));
      if (field !== 'btc') setAmountBtc(btc.toFixed(8).replace('.', ','));
      if (field !== 'fiat') {
        setAmountFiat(rate ? formatNumber((btc * rate).toFixed(2)) : '');
      }
      if (field !== 'fiatCompare' && compareCurrency) {
        setAmountFiatCompare(rateCmp ? formatNumber((btc * rateCmp).toFixed(2)) : '');
      }
    },
    [rates, currency, compareCurrency, formatNumber, clearFields]
  );

  const handleSatsChange = useCallback(
    (e) => {
      const digits = e.target.value.replace(/\./g, '').replace(/,/g, '');
      setActiveField('sats');
      if (digits === '') {
        clearFields();
        setActiveField('sats');
        return;
      }
      const n = Number(digits);
      if (Number.isNaN(n)) return;
      setAmountSats(n.toLocaleString('de-DE'));
      recalcFrom('sats', digits);
    },
    [recalcFrom, clearFields]
  );

  const handleBtcChange = useCallback(
    (e) => {
      const raw = e.target.value.replace(',', '.');
      if (!/^\d*\.?\d*$/.test(raw)) return;
      setActiveField('btc');
      setAmountBtc(e.target.value);
      if (raw === '') {
        clearFields();
        setActiveField('btc');
        return;
      }
      recalcFrom('btc', e.target.value);
    },
    [recalcFrom, clearFields]
  );

  const handleFiatChange = useCallback(
    (e) => {
      const v = e.target.value.replace(/[^0-9,]/g, '');
      setActiveField('fiat');
      setAmountFiat(v);
      if (v === '') {
        clearFields();
        setActiveField('fiat');
        return;
      }
      recalcFrom('fiat', v);
    },
    [recalcFrom, clearFields]
  );

  const handleFiatCompareChange = useCallback(
    (e) => {
      const v = e.target.value.replace(/[^0-9,]/g, '');
      setActiveField('fiatCompare');
      setAmountFiatCompare(v);
      if (v === '') {
        clearFields();
        setActiveField('fiatCompare');
        return;
      }
      recalcFrom('fiatCompare', v);
    },
    [recalcFrom, clearFields]
  );

  // Recalcula cuando cambian las tasas o las monedas, manteniendo el campo
  // que el usuario usó como referencia.
  useEffect(() => {
    if (!activeField || isLoading) return;
    const rawMap = {
      sats: amountSats,
      btc: amountBtc,
      fiat: amountFiat,
      fiatCompare: amountFiatCompare,
    };
    recalcFrom(activeField, rawMap[activeField]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates, currency, compareCurrency, isLoading]);

  useEffect(() => {
    const fetchMempoolFee = async () => {
      try {
        const response = await fetch('https://mempool.space/api/v1/fees/recommended');
        const feeData = await response.json();
        setOnChainFee(feeData.fastestFee);
      } catch (err) {
        console.error('Error fetching mempool data:', err);
      }
    };

    fetchMempoolFee();
  }, []);

  const handleFiatBlur = useCallback(() => {
    if (amountFiat) {
      const num = parseFloat(amountFiat.replace(/\./g, '').replace(',', '.'));
      if (!Number.isNaN(num)) setAmountFiat(formatNumber(num.toFixed(2)));
    }
  }, [amountFiat, formatNumber]);

  const handleFiatCompareBlur = useCallback(() => {
    if (amountFiatCompare) {
      const num = parseFloat(amountFiatCompare.replace(/\./g, '').replace(',', '.'));
      if (!Number.isNaN(num)) setAmountFiatCompare(formatNumber(num.toFixed(2)));
    }
  }, [amountFiatCompare, formatNumber]);

  const handleBtcBlur = useCallback(() => {
    if (amountBtc) {
      const num = parseFloat(amountBtc.replace(',', '.'));
      if (!Number.isNaN(num)) setAmountBtc(num.toFixed(8).replace('.', ','));
    }
  }, [amountBtc]);

  const handleCurrencyChange = useCallback((e) => {
    setCurrency(e.target.value);
  }, []);

  const handleCompareCurrencyChange = useCallback((e) => {
    setCompareCurrency(e.target.value);
  }, []);

  const handleFocus = useCallback((e) => {
    if (e.target.value === '0' || e.target.value === '0,00') {
      e.target.value = '';
    }
  }, []);

  const addCompareField = useCallback(() => {
    setCompareCurrency((prev) => prev || (currency === 'EUR' ? 'USD' : 'EUR'));
  }, [currency]);

  const removeCompareField = useCallback(() => {
    setCompareCurrency(null);
    setAmountFiatCompare('');
    if (activeField === 'fiatCompare') setActiveField(null);
  }, [activeField]);

  const calculateDisplayedRate = useCallback(() => {
    const rate = rates[currency];
    if (!rate) return error ? 'Error' : 'Cargando...';
    return formatNumber(rate.toFixed(2));
  }, [rates, currency, error, formatNumber]);

  return (
    <div className="container">
      <div className="top-bar">
        <ThemeSwitch value={themeMode} onChange={setThemeMode} />
      </div>

      <Banner />

      <button className="button" type="button">
        <div className="btc-price">
          <em>
            Bitcoin BTC / {calculateDisplayedRate()} {currency}
          </em>
        </div>
        <div className="mempool-fees">
          <p>On-chain Fee: {onChainFee ? `${onChainFee} sat/vB` : 'Cargando...'}</p>
        </div>
      </button>

      {error && <div className="rate-error">{error}</div>}

      <div className="input-group">
        <input
          type="text"
          inputMode="numeric"
          value={amountSats}
          onFocus={handleFocus}
          onChange={handleSatsChange}
          placeholder="Satoshis"
          aria-label="Cantidad en Satoshis"
        />
        <span>Sat</span>
        <img src={satFlag} alt="Sat icon" />
        <button onClick={clearFields} aria-label="Limpiar">
          x
        </button>
      </div>

      <div className="input-group">
        <input
          type="text"
          inputMode="decimal"
          value={amountBtc}
          onFocus={handleFocus}
          onChange={handleBtcChange}
          onBlur={handleBtcBlur}
          placeholder="Bitcoin"
          aria-label="Cantidad en Bitcoin"
        />
        <span>BTC</span>
        <img src={btcFlag} alt="BTC icon" />
        <button onClick={clearFields} aria-label="Limpiar">
          x
        </button>
      </div>

      <div className="input-group">
        <input
          type="text"
          inputMode="decimal"
          value={amountFiat}
          onFocus={handleFocus}
          onChange={handleFiatChange}
          onBlur={handleFiatBlur}
          placeholder="Cantidad en Fiat"
          aria-label={`Cantidad en ${currency}`}
        />
        <select
          value={currency}
          onChange={handleCurrencyChange}
          disabled={isLoading}
          aria-label="Moneda fiat principal"
        >
          {CURRENCIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code}
            </option>
          ))}
        </select>
        <CurrencyFlag code={currency} />
        <button onClick={clearFields} aria-label="Limpiar">
          x
        </button>
      </div>

      {compareCurrency && (
        <div className="input-group compare-group">
          <input
            type="text"
            inputMode="decimal"
            value={amountFiatCompare}
            onFocus={handleFocus}
            onChange={handleFiatCompareChange}
            onBlur={handleFiatCompareBlur}
            placeholder="Comparar en Fiat"
            aria-label={`Cantidad en ${compareCurrency}`}
          />
          <select
            value={compareCurrency}
            onChange={handleCompareCurrencyChange}
            disabled={isLoading}
            aria-label="Moneda fiat de comparación"
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
          </select>
          <CurrencyFlag code={compareCurrency} />
          <button onClick={removeCompareField} aria-label="Quitar comparación" title="Quitar comparación">
            x
          </button>
        </div>
      )}

      {!compareCurrency && (
        <button className="add-compare" type="button" onClick={addCompareField}>
          + Agregar comparación
        </button>
      )}

      <div className="text-credit">
        <p>
          Tipo de cambio proporcionado por{' '}
          <a href="https://www.coingecko.com/es/api" target="_blank" rel="noreferrer">
            CoinGecko
          </a>
          .
        </p>
        <p>
          Tipo de cambio para ARS, Dólar blue promedio, proporcionado por
          <a href="https://bluelytics.com.ar/" target="_blank" rel="noreferrer">
            {' '}
            Bluelytics
          </a>
          .
        </p>
        <p>
          Tipo de cambio para COP, UYU y PEN proporcionado por{' '}
          <a href="https://yadio.io/" target="_blank" rel="noreferrer">
            Yadio
          </a>
          .
        </p>
      </div>
      <BoostMeCard />
    </div>
  );
}

export default App;
